import { TCheckoutMgr } from 'widgets/checkout/CheckoutMgr';
import { TAjaxFormInstance } from 'widgets/forms/AjaxForm';
import { TInputEmailInstance } from 'widgets/forms/InputEmail';
import { TCustomerRegistrationInstance } from 'widgets/checkout/CustomerRegistration';
import { TButtonInstance } from 'widgets/global/Button';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { timeout } from 'widgets/toolbox/util';

type TSocialMediaLinkConfig = {
    providerId: string;
};

/**
 * @description Checkout Login step logic
 *
 * @param CheckoutMgr Base widget for extending
 */
function CustomerStepMixinClassCreator(CheckoutMgr: TCheckoutMgr) {
    // TODO: refactor this widget to move social login logic to its own cartridge
    /**
     * @class CustomerStepMixin
     * @augments CheckoutMgr
     * @subcategory checkout
     * @classdesc Represents customer step component with the next features:
     * 1. Manage Login step state on the checkout
     * 2. Submit customer form
     */
    class CustomerStepMixin extends CheckoutMgr {
        registeredCustomerLoginForm;

        hasOauthProviderSavedData = false;

        prefs() {
            return {
                customerEmailInputId: '',
                guestEmailInputId: '',
                urlGuestCheckout: '',
                customerLoginAlerts: {
                    selectedAlert: '',
                    selectedAlertType: ''
                },
                ...super.prefs()
            };
        }

        /**
         * @description Activate customer step hook
         * Checks if email is already provided and either renders guest email form or login form
         */
        customerActivate() {
            this.getById('loginForm', (checkoutLoginForm) => {
                this.registeredCustomerLoginForm = checkoutLoginForm;
            });

            if (this.prefs().displayCustomerForm || this.order?.orderEmail) {
                this.activateRegisteredLoginForm();
            } else {
                this.activateGuestLoginForm();
            }

            this.toggleCustomerRegistrationFlow();

            this.eventBus().emit('checkout.customer.step.activated', this);
        }

        customerDeActivate() {
            // Empty method, needed for consistency of all steps in CheckoutMgr
        }

        /**
         * @description Handler of guest email form submission
         * Checks if user is registered and renders login form in this case
         * Or proceeds to next step if user is a guest
         *
         * @param form - submitted guest email form
         * @param response - serverResponse
         */
        onCustomerVerificationSubmit(form, response) {
            this.order = response.order;
            this.hasOauthProviderSavedData = response.hasOauthProviderSavedData;
            this.prefs().customerLoginAlerts.selectedAlert = '';

            if (response.order?.customer?.isRegistered) {
                this.prefillCustomerEmail(form);
                this.activateRegisteredLoginForm();
            } else if (this.prefs().isGuestCheckoutFlowRestricted) {
                this.activateCustomerRegistrationFlow(this.order?.orderEmail || '');
            } else {
                this.callIfExists('updateSummaryAndNext', form, response);
            }

            this.eventBus().emit('customeremail.provided', form.getFormFields()[this.prefs().guestEmailInputId]);
        }

        /**
         * @description Toggles customer registration flow in case guest flow is restricted
         * @param state - Use true to show the element or false to hide it.
         */
        toggleCustomerRegistrationFlow(state = false): TCustomerRegistrationInstance | undefined | void {
            return this.getById<TCustomerRegistrationInstance>(
                'customerRegistration',
                (customerRegistrationFlow) => {
                    return customerRegistrationFlow?.toggle(state);
                }
            );
        }

        /**
         * @description Activates customer registration flow in case guest flow is restricted
         * @param email - Customer email to prefill
         */
        activateCustomerRegistrationFlow(email = '') {
            this.getById('guestLogin', (guestLoginForm: TAjaxFormInstance) => guestLoginForm.hide());
            this.registeredCustomerLoginForm?.hide();
            this.toggleCustomerRegistrationFlow(true)?.prefillEmailField(email);
        }

        /**
         * @description Pre fill login form with the email, that user provided in guest form
         * @param form - guest email form
         */
        prefillCustomerEmail(form) {
            this.registeredCustomerLoginForm?.getById(this.prefs().customerEmailInputId, (emailInput: TInputEmailInstance) => {
                emailInput.setValue(form.getFormFields()[this.prefs().guestEmailInputId]);
            });
        }

        /**
         * @description Guest submission result handler
         *
         * @param button - guest checkout button instance
         */
        skipLogin(button: TButtonInstance) {
            button.busy();

            submitFormJson(this.prefs().urlGuestCheckout)
                .then((res) => {
                    if (res.success) {
                        this.nextStep();
                    }
                }).finally(() => {
                    button.unbusy();
                });
        }

        /**
         * @description Render guest email form instead of login form
         *
         * @param clearPreviousState - flag that indicates if requiredEmailState should be reset
         */
        activateGuestLoginForm(clearPreviousState = false) {
            this.getById('guestLogin', (guestLoginForm: TAjaxFormInstance) => {
                guestLoginForm.show();

                if (clearPreviousState) {
                    guestLoginForm.ref('requiredEmailState').hide();
                    guestLoginForm.ref('socialSignIn').show();
                }
            });
            this.registeredCustomerLoginForm?.hide();
        }

        /**
         * Render login form instead of guest email form
         */
        activateRegisteredLoginForm() {
            this.getById('guestLogin', (guestLoginForm: TAjaxFormInstance) => guestLoginForm.hide());
            this.registeredCustomerLoginForm?.show();

            timeout(() => this.callIfExists('focusFirst', 'customerLoginSection', [], true), 200);

            if (!this.order?.customer) {
                return;
            }

            this.displayLoginFormFields(this.order.customer.isExternallyAuthenticated);
            this.displaySocialLoginButtons(this.order.customer.linkedExternalProfiles, this.order.customer.linkedSocialMedia);

            this.showLoginAlert();
        }

        /**
         * @description Based on the customer registration info (is he/she externally authenticated or not) display social media or password auth fields
         *
         * @param isExternallyAuthenticated customer registration status
         */
        displayLoginFormFields(isExternallyAuthenticated) {
            this.registeredCustomerLoginForm?.ref('passwordActions').toggle(!isExternallyAuthenticated);
            this.registeredCustomerLoginForm?.ref('socialMediaGuestActions').toggle(isExternallyAuthenticated);
        }

        /**
         * @description Based on the list of social media linked to the customer account, display only buttons for available social medias authentication
         *
         * @param linkedExternalProfiles Array of all configured social media for login
         * @param linkedSocialMedia Array of social media that customer linked to his/her account
         */
        displaySocialLoginButtons(
            linkedExternalProfiles = [] as Array<TSocialMediaLinkConfig>,
            linkedSocialMedia = [] as Array<TSocialMediaLinkConfig>
        ) {
            this.registeredCustomerLoginForm?.ref('customerSocialLinks').toggle(linkedExternalProfiles.length);

            if (linkedExternalProfiles.length) {
                linkedSocialMedia.forEach((socialMedia) => {
                    const isSocialMediaLinkedByCustomer = linkedExternalProfiles
                        .find(externalProfile => externalProfile.providerId === socialMedia.providerId);

                    this.registeredCustomerLoginForm?.ref('socialLink' + socialMedia.providerId).toggle(!!isSocialMediaLinkedByCustomer);
                });
            }
        }

        /**
         * @description Show the correct login alert (warning or static message) and set the proper text to it.
         */
        showLoginAlert() {
            let selected = '';

            if (this.prefs().customerLoginAlerts.selectedAlert) {
                selected = this.prefs().customerLoginAlerts.selectedAlert;
            } else if (this.hasOauthProviderSavedData) {
                selected = this.order?.customer?.isExternallyAuthenticated ? 'useSocialMedia' : 'useExistingCredential';
            } else {
                selected = this.order?.customer?.isExternallyAuthenticated ? 'emailIsRegistered' : 'passwordLogin';
            }

            const alertTypeRef = ['passwordLogin', 'emailIsRegistered'].includes(selected) ? 'staticAlert' : 'warningMessage';

            this.registeredCustomerLoginForm?.ref(alertTypeRef).setText(this.prefs().customerLoginAlerts[selected]);
            this.registeredCustomerLoginForm?.ref('warningMessage').toggle(alertTypeRef === 'warningMessage');
            this.registeredCustomerLoginForm?.ref('staticAlert').toggle(alertTypeRef === 'staticAlert');
        }
    }

    return CustomerStepMixin;
}

export type TCustomerStepMixin = ReturnType<typeof CustomerStepMixinClassCreator>;

export type TCustomerStepMixinInstance = InstanceType<TCustomerStepMixin>;

export default CustomerStepMixinClassCreator;
